import type { Automation, AutomationInput, AutomationTool, CustomerSkill, CustomerSkillDetail, DataListing, FeaturedSkill, IntegrationStatus, RunSummary, ServerEvent } from '@harness/shared/protocol';

export interface Me {
  name: string;
  email: string;
  projects: { id: string; name: string }[];
}

export interface ChatSummary {
  id: string;
  title: string;
  updatedAt: number;
}

export type UploadResult = { path: string } | { error: number };

const project = (id: string): string => `/p/${encodeURIComponent(id)}`;

/** The signed-in person, or null if the gateway says there is no session. */
export async function fetchMe(): Promise<Me | null> {
  const response = await fetch('/api/me', { headers: { accept: 'application/json' } });
  if (response.status === 401) return null;
  if (!response.ok) throw new Error(`GET /api/me ${response.status}`);
  return (await response.json()) as Me;
}

export async function fetchChats(projectId: string): Promise<ChatSummary[]> {
  const response = await fetch(`${project(projectId)}/chats`);
  if (!response.ok) throw new Error(`GET chats ${response.status}`);
  return (await response.json()) as ChatSummary[];
}

/** The stored events of one of the consultant's chats, or null if it does not exist. */
export async function fetchEvents(projectId: string, chatId: string): Promise<ServerEvent[] | null> {
  const response = await fetch(`${project(projectId)}/chats/${encodeURIComponent(chatId)}/events`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`GET events ${response.status}`);
  return (await response.json()) as ServerEvent[];
}

/** The consultant's own connections to the integrations of this project. */
export async function fetchIntegrations(projectId: string): Promise<IntegrationStatus[]> {
  const response = await fetch(`${project(projectId)}/integrations`);
  if (!response.ok) throw new Error(`GET integrations ${response.status}`);
  return (await response.json()) as IntegrationStatus[];
}

/** Standard skills offered as chips above the input field. */
export async function fetchFeaturedSkills(projectId: string): Promise<FeaturedSkill[]> {
  const response = await fetch(`${project(projectId)}/featured-skills`);
  if (!response.ok) throw new Error(`GET featured-skills ${response.status}`);
  return (await response.json()) as FeaturedSkill[];
}

/** Opened as a plain link in a new tab; the worker redirects to the provider's login. */
export function connectUrl(projectId: string, integrationId: string): string {
  return `${project(projectId)}/integrations/${encodeURIComponent(integrationId)}/connect`;
}

/** True if the connection was removed. */
export async function disconnectIntegration(projectId: string, integrationId: string): Promise<boolean> {
  const response = await fetch(`${project(projectId)}/integrations/${encodeURIComponent(integrationId)}`, { method: 'DELETE' });
  return response.ok;
}

/** All automations of the project; every consultant of the customer sees all of them. */
export async function fetchAutomations(projectId: string): Promise<Automation[]> {
  const response = await fetch(`${project(projectId)}/automations`);
  if (!response.ok) throw new Error(`GET automations ${response.status}`);
  return (await response.json()) as Automation[];
}

/** Writing integration tools an owner can allow for an automation. */
export async function fetchAutomationTools(projectId: string): Promise<AutomationTool[]> {
  const response = await fetch(`${project(projectId)}/automations/tools`);
  if (!response.ok) throw new Error(`GET automation tools ${response.status}`);
  return (await response.json()) as AutomationTool[];
}

/** The worker's answer to a change: the automation, or an error code (e.g. `owner-change`, `tool-not-allowed`). */
export type AutomationResult = { ok: true; automation: Automation } | { ok: false; error: string; detail: string | null };

async function automationResult(response: Response): Promise<AutomationResult> {
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (response.ok) return { ok: true, automation: body as unknown as Automation };
  return { ok: false, error: typeof body.error === 'string' ? body.error : String(response.status), detail: typeof body.detail === 'string' ? body.detail : null };
}

const automationUrl = (projectId: string, id: string): string => `${project(projectId)}/automations/${encodeURIComponent(id)}`;

/** Creates an automation (id null) or saves a change; `takeOver` confirms becoming the owner. */
export async function saveAutomation(projectId: string, id: string | null, input: AutomationInput, takeOver = false): Promise<AutomationResult> {
  const response = await fetch(id === null ? `${project(projectId)}/automations` : automationUrl(projectId, id), {
    method: id === null ? 'POST' : 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(id === null ? input : { ...input, takeOver }),
  });
  return automationResult(response);
}

export async function automationAction(projectId: string, id: string, action: 'confirm-tools' | 'take-over' | 'pause' | 'resume'): Promise<AutomationResult> {
  return automationResult(await fetch(`${automationUrl(projectId, id)}/${action}`, { method: 'POST' }));
}

/** True if the run was started or noted (skipped, busy); false if the automation is paused or gone. */
export async function runAutomationNow(projectId: string, id: string): Promise<boolean> {
  return (await fetch(`${automationUrl(projectId, id)}/run`, { method: 'POST' })).ok;
}

export async function deleteAutomation(projectId: string, id: string): Promise<boolean> {
  return (await fetch(automationUrl(projectId, id), { method: 'DELETE' })).ok;
}

export async function fetchRuns(projectId: string, id: string): Promise<RunSummary[]> {
  const response = await fetch(`${automationUrl(projectId, id)}/runs`);
  if (!response.ok) throw new Error(`GET runs ${response.status}`);
  return (await response.json()) as RunSummary[];
}

/** The latest runs of all automations, newest first. */
export async function fetchAllRuns(projectId: string): Promise<RunSummary[]> {
  const response = await fetch(`${project(projectId)}/automations/runs`);
  if (!response.ok) throw new Error(`GET all runs ${response.status}`);
  return (await response.json()) as RunSummary[];
}

/** One run, or null if it does not belong to this automation (any more). */
export async function fetchRun(projectId: string, id: string, runId: string): Promise<RunSummary | null> {
  const response = await fetch(`${automationUrl(projectId, id)}/runs/${encodeURIComponent(runId)}`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`GET run ${response.status}`);
  return (await response.json()) as RunSummary;
}

/** The course of one run, as chat events with the run's id as `chatId`. */
export async function fetchRunEvents(projectId: string, id: string, runId: string): Promise<ServerEvent[]> {
  const response = await fetch(`${automationUrl(projectId, id)}/runs/${encodeURIComponent(runId)}/events`);
  if (!response.ok) throw new Error(`GET run events ${response.status}`);
  return (await response.json()) as ServerEvent[];
}

/** The customer data: all files of the project, shared by its consultants. */
export async function fetchData(projectId: string): Promise<DataListing> {
  const response = await fetch(`${project(projectId)}/data`);
  if (!response.ok) throw new Error(`GET data ${response.status}`);
  return (await response.json()) as DataListing;
}

export function dataFileUrl(projectId: string, name: string): string {
  return `${project(projectId)}/data/${encodeURIComponent(name)}`;
}

/** True if the file was deleted. */
export async function deleteDataFile(projectId: string, name: string): Promise<boolean> {
  return (await fetch(dataFileUrl(projectId, name), { method: 'DELETE' })).ok;
}

/** The worker's error code (e.g. `exists`, `quota`), or `failed` if there was none. */
export type DataUploadResult = { ok: true; name: string } | { ok: false; error: string };

/** Uploads into the customer data with progress; an existing file is only replaced with `replace`. */
export function uploadDataFile(projectId: string, file: File, replace: boolean, onProgress?: (fraction: number) => void): Promise<DataUploadResult> {
  return new Promise((resolve) => {
    const request = new XMLHttpRequest();
    request.open('PUT', `${dataFileUrl(projectId, file.name)}${replace ? '?replace=true' : ''}`);
    request.upload.onprogress = (event): void => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    request.onload = (): void => {
      let body: Record<string, unknown> = {};
      try {
        body = JSON.parse(request.responseText) as Record<string, unknown>;
      } catch {
        // e.g. an error page of a proxy
      }
      if (request.status === 201 && typeof body.name === 'string') resolve({ ok: true, name: body.name });
      else resolve({ ok: false, error: typeof body.error === 'string' ? body.error : 'failed' });
    };
    request.onerror = (): void => resolve({ ok: false, error: 'failed' });
    request.send(file);
  });
}

/** The customer skills, shared by all consultants of the project. */
export async function fetchSkills(projectId: string): Promise<CustomerSkill[]> {
  const response = await fetch(`${project(projectId)}/skills`);
  if (!response.ok) throw new Error(`GET skills ${response.status}`);
  return (await response.json()) as CustomerSkill[];
}

function skillUrl(projectId: string, name: string): string {
  return `${project(projectId)}/skills/${encodeURIComponent(name)}`;
}

/** One skill with its text, or null if it does not exist (any more). */
export async function fetchSkill(projectId: string, name: string): Promise<CustomerSkillDetail | null> {
  const response = await fetch(skillUrl(projectId, name));
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`GET skill ${response.status}`);
  return (await response.json()) as CustomerSkillDetail;
}

/** `problems` are the worker's sentences, e.g. which frontmatter fields are not allowed; empty if it gave none. */
export type SkillSaveResult = { ok: true; skill: CustomerSkill } | { ok: false; problems: string[] };

async function saveResult(response: Response): Promise<SkillSaveResult> {
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (response.ok) return { ok: true, skill: body as unknown as CustomerSkill };
  const problems = Array.isArray(body.problems) ? body.problems.filter((problem): problem is string => typeof problem === 'string') : [];
  return { ok: false, problems };
}

export async function createSkill(projectId: string, text: string): Promise<SkillSaveResult> {
  return saveResult(await fetch(`${project(projectId)}/skills`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text }) }));
}

/** `changedAt` is the state the consultant opened; the worker refuses if someone saved in between. */
export async function updateSkill(projectId: string, name: string, text: string, changedAt: number): Promise<SkillSaveResult> {
  return saveResult(await fetch(skillUrl(projectId, name), { method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text, changedAt }) }));
}

/** True if the skill was deleted. */
export async function deleteSkill(projectId: string, name: string): Promise<boolean> {
  return (await fetch(skillUrl(projectId, name), { method: 'DELETE' })).ok;
}

export function downloadUrl(projectId: string, path: string): string {
  return `${project(projectId)}/files/${path.split('/').map(encodeURIComponent).join('/')}`;
}

export function socketUrl(projectId: string, location: Pick<Location, 'protocol' | 'host'>): string {
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}${project(projectId)}/ws`;
}

/** Uploads into discovery/ with progress; XMLHttpRequest because fetch reports no upload progress. */
export function uploadFile(projectId: string, file: File, onProgress?: (fraction: number) => void): Promise<UploadResult> {
  return new Promise((resolve) => {
    const request = new XMLHttpRequest();
    request.open('PUT', `${project(projectId)}/upload/${encodeURIComponent(file.name)}`);
    request.upload.onprogress = (event): void => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    };
    request.onload = (): void => {
      if (request.status !== 201) return resolve({ error: request.status });
      try {
        resolve({ path: (JSON.parse(request.responseText) as { path: string }).path });
      } catch {
        resolve({ error: 0 });
      }
    };
    request.onerror = (): void => resolve({ error: 0 });
    request.send(file);
  });
}
